import { FullSizeContainer } from '@mentimeter/visualizations-react/utils/full-size-container';
import { IdentitiesListModal } from '@mentimeter/slides-presentation-ui/identified-responses/modal';
import { IdentitiesList } from '@mentimeter/slides-presentation-ui/identified-responses/list';
import { PieVisualization } from '@mentimeter/visualizations-react/visualizations/pie';
import type { OpenedIdentifiedResponsesListEvent } from '@mentimeter/question-modules-types';
import { useRagnar } from '@mentimeter/ragnar-react';
import { useTranslations } from 'next-intl';
import { trackUser } from '@api/tracking/client';
import type { Choice, ChoicesVisualizationProps } from '../types';
import {
  getIdentitiesWithNameFallback,
  getParticipantsWithNameFallback,
} from '../utils/getIdentitiesWithNameFallback';
import { useIdentifiedResultsListState } from './useIdentifiedResultsListState';

export function PieDataHandler({
  showCorrect,
  results,
  choices,
  shouldShowPercent,
  identifiedResults,
  shouldHideResults,
  visualizationType,
  shouldAnimate,
}: ChoicesVisualizationProps & {
  visualizationType: 'pie' | 'donut';
}) {
  const {
    theme: {
      visualizationColors: { fillColors, textColor },
      fonts: { body },
    },
  } = useRagnar();
  const { results: currentResults } = results;
  const translate = useTranslations('presenting.legacy');

  const visualizationData = currentResults.map((result) => ({
    id: result.id,
    score: result.score.reduce((a, b) => a + b, 0),
  }));

  const colors = fillColors.length > 0 ? fillColors : [textColor];

  const handleShowIdentitiesList = (choiceId: Choice['id']) => {
    trackUser({
      event: 'Opened identified responses list',
      properties: {
        'slide type': 'choices',
        visualization: visualizationType,
        placement: 'Visualization',
        context: 'Presentation view',
      },
    } satisfies OpenedIdentifiedResponsesListEvent);
    setIdentitiesListChoiceId(choiceId);
  };

  const {
    setIdentitiesListChoiceId,
    identityListChoice,
    identityListChoiceIndex,
    activeChoiceIdentities,
  } = useIdentifiedResultsListState({
    choices,
    identifiedResults: identifiedResults ?? [],
    shouldHideResults,
  });

  const shouldShowIdentitiesList = Boolean(
    identityListChoice !== undefined &&
      activeChoiceIdentities?.participants?.length &&
      activeChoiceIdentities.participants?.length > 0 &&
      visualizationData.length > 0,
  );

  const totalScore =
    visualizationData
      .map(({ score }) => score)
      .reduce((acc, cur) => acc + cur, 0) || 1;

  return (
    <>
      <FullSizeContainer>
        {(width, height) => (
          <PieVisualization
            colors={colors as [string, ...[string]]}
            textColor={textColor}
            data={visualizationData}
            staticOptions={choices}
            width={width}
            height={height}
            shouldShowCorrect={showCorrect}
            shouldShowScorePercentage={
              shouldShowPercent ? { percentOf: totalScore } : false
            }
            isDonut={visualizationType === 'donut'}
            disableAnimations={!shouldAnimate}
            identities={getIdentitiesWithNameFallback(identifiedResults) ?? []}
            onClickOption={
              identifiedResults === null ? undefined : handleShowIdentitiesList
            }
            fontFamily={body}
          />
        )}
      </FullSizeContainer>
      {shouldShowIdentitiesList && (
        <IdentitiesListModal onDismiss={() => setIdentitiesListChoiceId(null)}>
          <IdentitiesList
            identities={
              getParticipantsWithNameFallback(
                activeChoiceIdentities?.participants,
              ) ?? []
            }
            color={
              fillColors[identityListChoiceIndex % fillColors.length] ?? '#000'
            }
            title={identityListChoice?.label ?? 'Choice not found'}
            totalResponses={activeChoiceIdentities?.responseCount ?? 0}
            translationFn={translate}
          />
        </IdentitiesListModal>
      )}
    </>
  );
}
